/**
 * Spanish copy, transcribed from "06 - Build Passing - site".
 *
 * Two things here are deliberate and must survive any edit.
 *
 * **Usted, never tú.** This is a business talking to another business about a
 * contract worth six figures in pesos. The product sites use tú because they
 * talk to a person about their groceries; this one does not.
 *
 * **Odoo leads the services.** The English page opens with web platforms; this
 * one opens with Odoo and ERP, because that is what a Mexican company arrives
 * looking for, and CFDI and Mexican fiscal requirements are named explicitly.
 * The order of `services.items` and of `proposal.kinds` differs from English on
 * purpose — the stored values do not, so requests stay comparable.
 */
import type { Dictionary } from './types';

const es: Dictionary = {
  meta: {
    title: 'Build Passing — estudio de software en México, trabajando en México y Estados Unidos',
    description:
      'Desarrollamos el software con el que opera su empresa: plataformas web, aplicaciones móviles, Odoo y APIs. Alcance y precio fijos, y el código es suyo desde el primer commit.',
  },

  nav: {
    services: 'servicios',
    products: 'productos',
    method: 'método',
    studio: 'estudio',
    cta: 'solicitar propuesta',
    menu: 'Menú',
    closeMenu: 'Cerrar menú',
    languageLabel: 'Idioma',
    skipToContent: 'Saltar al contenido',
  },

  hero: {
    eyebrow: 'Estudio de software · México · trabajamos en México y Estados Unidos',
    headline:
      'Desarrollamos el software con el que opera su empresa. Y el nuestro, para demostrarlo.',
    body: 'Plataformas web, aplicaciones móviles y Odoo, con alcance y precio fijos. El código es suyo desde el primer commit.',
    ctaPrimary: 'Solicitar propuesta',
    ctaSecondary: 'ver nuestros productos',
    stack: [
      'Odoo ERP · CFDI',
      'Python · Django · Flask',
      'Node.js · APIs REST',
      'React · TypeScript',
      'Flutter · iOS y Android',
    ],
    productsNote: '3 productos en la línea',
  },

  services: {
    eyebrow: 'lo que desarrollamos',
    title: 'Cuatro prácticas, cotizadas por separado',
    lead: 'Las cotizamos por separado y con precio fijo, para que sepa cuánto cuesta cada etapa antes de que empiece.',
    items: [
      {
        num: '01',
        label: 'odoo y erp',
        title: 'Odoo a la medida de su operación',
        body: 'Implementación, módulos a medida y migración de datos para empresas que ya superaron las hojas de cálculo, con facturación y requisitos fiscales mexicanos resueltos, y sin forzar su operación a la configuración por defecto de alguien más.',
      },
      {
        num: '02',
        label: 'plataformas web',
        title: 'Sistemas con los que opera el negocio',
        body: 'Herramientas internas, portales para clientes, tableros y sistemas administrativos. Django, Flask o Node y Express en el backend, React y TypeScript en el frontend, PostgreSQL debajo. El software poco glamoroso del que una empresa realmente depende.',
      },
      {
        num: '03',
        label: 'móvil',
        title: 'iOS y Android, un solo código',
        body: 'Aplicaciones en Flutter, desde el diseño hasta la publicación en tiendas y la gestión de versiones. Así publicamos nuestras propias apps, así que es un proceso que ejecutamos cada mes, no de vez en cuando.',
      },
      {
        num: '04',
        label: 'apis e integraciones',
        title: 'APIs que construimos, y APIs que domamos',
        body: 'Diseñamos y construimos APIs REST en Node o Python: versionadas, documentadas y autenticadas. E integramos las que ya tiene que soportar. Webhooks, límites de tasa, reintentos y los casos de falla que nadie documenta, para que su CRM, contabilidad, tienda y ERP por fin coincidan.',
      },
    ],
  },

  products: {
    eyebrow: 'nuestra línea de productos · uno en la App Store, dos en camino',
    title: 'Somos nuestro cliente más exigente',
    lead: 'La mayoría de los estudios sólo construyen con el presupuesto de alguien más. Nosotros financiamos y publicamos productos propios, así que nuestro proceso de lanzamiento, el manejo de tiendas y la disciplina de mantenimiento están probados en software del que no podemos desentendernos.',
    items: [
      {
        name: 'Rickey',
        status: 'en la App Store',
        body: 'Una app de coctelería que responde bien a una sola pregunta: ¿qué puede preparar hoy con las botellas que ya tiene en casa?',
        progress: 'publicada en la App Store · landing en línea',
        imageAlt: 'Landing de Rickey: el menú de hoy, con lo que ya tiene',
      },
      {
        name: 'TwoCoins',
        status: 'en desarrollo',
        body: 'Un administrador de finanzas personales para quienes se rindieron con las hojas de cálculo: cuentas, categorías y una respuesta honesta sobre a dónde se fue el mes.',
        progress: 'en desarrollo · se publicará como PWA en twocoins.app',
        imageAlt: 'Landing de TwoCoins: hecha para cómo funciona el crédito en México',
      },
      {
        name: 'Two Milks',
        status: 'próxima en salir',
        body: 'Despensa, pendientes y recordatorios para las personas con las que realmente vive: roomies, parejas, familias. Una sola lista, sincronizada, sin discutir quién compró la leche.',
        progress: 'cerca de publicarse · App Store en camino',
        imageAlt: 'Landing de Two Milks: una sola lista para toda la casa',
      },
    ],
    visit: 'ver sitio',
  },

  method: {
    eyebrow: 'cómo funciona un proyecto',
    title: 'Cuatro etapas, cada una cotizada por separado',
    lead: 'Cada etapa se cotiza y se factura por separado. Puede detenerse al terminar cualquier etapa y conservar todo lo producido hasta ese punto.',
    stages: [
      {
        num: '01',
        label: 'descubrimiento',
        title: 'Una semana, precio fijo',
        body: 'Aprendemos cómo se hace el trabajo hoy y entregamos un alcance por escrito con una cifra real. Si se detiene aquí, el documento es suyo y puede llevarlo a donde quiera.',
      },
      {
        num: '02',
        label: 'diseño',
        title: 'Pantallas antes que código',
        body: 'Pantallas navegables que puede poner frente a un usuario real antes de que exista código de producción. El lugar más barato para cambiar de opinión.',
      },
      {
        num: '03',
        label: 'desarrollo',
        title: 'Incrementos de dos semanas',
        body: 'Software funcionando en una URL real cada dos semanas, con una nota escrita de lo que cambió. Sin presentaciones de estatus y siempre con acceso directo al ingeniero.',
      },
      {
        num: '04',
        label: 'entrega',
        title: 'Publicación, documentación y llaves',
        body: 'Publicación en tiendas, monitoreo, documentación técnica escrita y todas las cuentas transferidas a su nombre. Nos contrata para mantenimiento o no. Nunca queda amarrado.',
      },
    ],
  },

  standards: {
    eyebrow: 'estándares',
    title: 'Incluido en todo proyecto',
    lead: 'No son extras. Son las condiciones bajo las que trabajamos, y quedan por escrito en el contrato.',
    items: [
      {
        title: 'El código es suyo',
        body: 'Repositorio, servidores, dominios y fichas de tienda en sus cuentas y a su nombre, desde el primer día.',
      },
      {
        title: 'Precio fijo por etapa',
        body: 'Acordado antes de empezar. Si estimamos mal, nosotros lo absorbemos. Usted nunca recibe una factura sorpresa.',
      },
      {
        title: 'Documentación escrita',
        body: 'Arquitectura, despliegue y operación, escritas para que su siguiente desarrollador continúe sin tener que llamarnos.',
      },
      {
        title: 'Respuesta en un día hábil',
        body: 'Durante un proyecto, cada mensaje recibe una respuesta real en menos de un día hábil. Por escrito, en el contrato.',
      },
      {
        title: 'Probado antes de publicar',
        body: 'Pruebas automatizadas en todo lo que toca dinero o datos, y un ambiente de pruebas que puede romper sin miedo.',
      },
      {
        title: '30 días de garantía',
        body: 'Todo lo que construimos y no se comporte como se especificó se corrige sin costo durante 30 días después de la publicación.',
      },
    ],
  },

  stack: {
    eyebrow: 'lo que usamos',
    groups: [
      { label: 'backend', value: 'Python · Django · Flask · Node.js · Express' },
      { label: 'frontend', value: 'React · TypeScript' },
      { label: 'móvil', value: 'Flutter · iOS y Android' },
      { label: 'datos', value: 'PostgreSQL · migraciones · respaldos' },
      { label: 'erp', value: 'Odoo · módulos a medida' },
      { label: 'interfaces', value: 'APIs REST · webhooks · integraciones con terceros' },
    ],
    note: 'Cada proyecto se entrega con staging, pruebas automatizadas en todo lo que toca dinero o datos, y documentación escrita para quien le dé mantenimiento después.',
  },

  studio: {
    title: 'Pequeño, senior y con equipo por proyecto',
    lead: 'Cada proyecto lo dirige un ingeniero senior con seis años de trabajo en producción con Python, Django, Flask, Node, React, Odoo y Flutter. Quien define su arquitectura está en todas las llamadas.',
    body: 'El estudio trabaja de forma continua bajo contrato con empresas de software en Estados Unidos y México, desarrollando sistemas en producción con Python y PostgreSQL y, actualmente, Node.js, Express y React con TypeScript. No damos nombres de clientes, pero por eso hablamos de equipos, husos horarios y estándares de revisión de código estadounidenses desde la experiencia y no desde la aspiración.',
    points: [
      {
        title: 'Sin nómina que mantener',
        body: 'Los especialistas se integran por proyecto desde una red con la que ya trabajamos: diseño, QA y desarrollo nativo. Paga sólo por las habilidades que su proyecto necesita.',
      },
      {
        title: 'Sin juniors asignados en silencio',
        body: 'Quien define el alcance del proyecto es quien lo construye.',
      },
      {
        title: 'Sin entregas a desconocidos',
        body: 'El equipo que publica es el mismo que conoció desde el inicio.',
      },
    ],
    facts: [
      { label: 'Fundado', value: '2026 · México' },
      { label: 'Idiomas', value: 'Español e inglés' },
      { label: 'Horario', value: 'CST · día hábil completo' },
    ],
  },

  engagements: {
    title: 'Tres formas de trabajar con nosotros',
    items: [
      {
        tag: 'alcance fijo',
        name: 'Proyecto',
        body: 'Un desarrollo definido, con alcance por escrito y precio fijo por etapa. Ideal cuando ya sabe qué necesita construir.',
        terms: '4–16 semanas',
      },
      {
        tag: 'mensual',
        name: 'Mensualidad',
        body: 'Un número fijo de días de ingeniería cada mes para desarrollo continuo, mantenimiento y publicaciones. Cancelable con 30 días de aviso.',
        terms: 'recurrente',
      },
      {
        tag: 'integrado',
        name: 'Con su equipo',
        body: 'Nos integramos a su equipo y a sus juntas diarias como capacidad senior por un periodo definido. Útil cuando ya tiene equipo, pero no suficiente.',
        terms: 'mín. 4 semanas',
      },
    ],
  },

  offer: {
    title: '30% de descuento en nuestros primeros tres proyectos',
    body: 'Estamos abriendo la práctica de clientes del estudio y queremos tres proyectos de referencia que podamos publicar con orgullo. A cambio de un caso de estudio que usted aprueba línea por línea, los primeros tres proyectos se facturan 30% por debajo de nuestra tarifa estándar.',
    points: [
      {
        title: 'Aplican todas las condiciones estándar.',
        body: 'Mismo precio fijo, misma documentación, misma garantía. El descuento sale de la tarifa, nunca del trabajo.',
      },
      {
        title: 'Usted aprueba cada palabra publicada.',
        body: 'Y si prefiere no aparecer con nombre, publicamos el caso de forma anónima o no lo publicamos.',
      },
      {
        title: 'Puede detenerse en cualquier etapa.',
        body: 'Se le factura el trabajo entregado que ya vio funcionando, y conserva todo lo producido.',
      },
    ],
    cta: 'Preguntar por un proyecto de lanzamiento',
  },

  proposal: {
    eyebrow: 'solicitar propuesta',
    title: 'Cuéntenos qué necesita construir',
    body: 'Cuéntenos qué necesita construir, o qué salió mal con lo que ya tiene. Cada solicitud la lee un ingeniero, no una bandeja de tickets, y se responde por escrito en menos de un día hábil. Y si no somos el estudio adecuado, se lo decimos y lo referimos con alguien mejor.',
    location: 'México · CST',
    fields: {
      name: 'Nombre',
      company: 'Empresa',
      optional: 'opcional',
      email: 'Correo de trabajo',
      kind: '¿Qué necesita?',
      budget: 'Presupuesto aproximado',
      deadline: 'Debe estar en línea para',
      detail: '¿Qué quiere construir?',
      detailHint: 'Entre más concreto, más útil será la primera respuesta.',
    },
    kinds: [
      { value: 'odoo', label: 'odoo / erp' },
      { value: 'web', label: 'plataforma web' },
      { value: 'mobile', label: 'app móvil' },
      { value: 'integrations', label: 'integraciones' },
      { value: 'rescue', label: 'rescatar un proyecto' },
    ],
    budgets: [
      { value: 'under-10k', label: 'Menos de $100,000 MXN' },
      { value: '10k-30k', label: '$100,000 – $250,000 MXN' },
      { value: '30k-75k', label: '$250,000 – $500,000 MXN' },
      { value: 'over-75k', label: 'Más de $500,000 MXN' },
      { value: 'guidance', label: 'Necesito orientación' },
    ],
    submit: 'enviar solicitud',
    sending: 'enviando…',
    reply: 'le contestamos hoy',
    successTitle: 'Solicitud recibida',
    successBody:
      'Un ingeniero la está leyendo. Tendrá una respuesta por escrito en menos de un día hábil, al correo que nos dio.',
    errors: {
      missing_name: 'Necesitamos un nombre para dirigir la respuesta.',
      missing_email: 'Agregue un correo para poder responderle.',
      invalid_email: 'Ese correo no parece correcto. Revíselo, por favor.',
      email_too_long: 'Ese correo es demasiado largo.',
      missing_detail: 'Cuéntenos un poco de lo que necesita construir — una o dos frases bastan.',
      detail_too_long:
        'Eso es más largo de lo que acepta el formulario. Acórtelo o escríbanos directo.',
      field_too_long: 'Una de esas respuestas es más larga de lo que acepta el formulario.',
      invalid_kind: 'Elija qué tipo de trabajo es, por favor.',
      invalid_budget: 'Elija un rango de presupuesto, o «Necesito orientación».',
      rate_limited:
        'Son muchas solicitudes desde un mismo lugar. Intente de nuevo en unos minutos.',
      spam_detected: 'Esa solicitud parecía automatizada. Si es una persona, escríbanos directo.',
      send_failed: 'No pudimos enviarla en este momento. Escríbanos directo y la retomamos.',
      server_error: 'Algo falló de nuestro lado. Escríbanos directo y la retomamos.',
    },
    offerNote: 'El precio de proyecto de lanzamiento sigue abierto — menciónelo y lo aplicamos.',
  },

  legal: {
    contents: 'Contenido',
    lastUpdated: 'Última actualización',
    effectiveDate: 'Fecha de entrada en vigor',
    bindingNotice:
      'El texto vinculante es la versión en inglés. Esta traducción se ofrece por conveniencia.',
    questionsTitle: 'Dudas',
    questionsBody: 'Escríbanos y le responde una persona.',
  },

  footer: {
    tagline: 'Estudio de software · México',
    rights: 'Todos los derechos reservados.',
    github: 'github',
    linkedin: 'linkedin',
    privacy: 'privacidad',
    cookies: 'cookies',
  },

  notFound: {
    eyebrow: 'no encontrado',
    title: 'No hay nada en esta dirección',
    body: 'La página que pidió no está aquí. Puede que se haya movido, o que el enlace tenga un error.',
    home: 'Volver al inicio',
    contact: 'Escríbanos',
    status: 'status · 404 not found',
    cta: 'Volver al inicio',
  },
};

export default es;
